// ═══════════════════════════════════════════════════════════════════════════════
// MICROSOFT GRAPH — SHARED MAILBOX INBOX
// ═══════════════════════════════════════════════════════════════════════════════
// Reads, replies to, and manages emails in the acquisitions@proximityparks.com
// shared mailbox. Every user with Full Access + Send As delegation can use
// these via their own Graph token + the shared mailbox scopes.

import { graphInboxRequest } from "../auth/msalConfig";

const SHARED_MAILBOX = "acquisitions@proximityparks.com";
const GRAPH_BASE = `https://graph.microsoft.com/v1.0/users/${SHARED_MAILBOX}`;

// ─── Token acquisition ───────────────────────────────────────────────────────

async function getToken(msalInstance) {
  const account = msalInstance.getActiveAccount() || msalInstance.getAllAccounts()[0];
  if (!account) throw new Error("No signed-in account.");
  try {
    const result = await msalInstance.acquireTokenSilent({ ...graphInboxRequest, account });
    return result.accessToken;
  } catch {
    await msalInstance.acquireTokenRedirect({ ...graphInboxRequest, account });
    throw new Error("Re-authentication required. Please retry after the page reloads.");
  }
}

async function graphFetch(msalInstance, url, options = {}) {
  const token = await getToken(msalInstance);
  const res = await fetch(url, {
    ...options,
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
      ...options.headers,
    },
  });
  if (!res.ok) {
    let detail = "";
    try { const j = await res.json(); detail = j?.error?.message || JSON.stringify(j); } catch { detail = await res.text().catch(() => ""); }
    throw new Error(`Graph ${options.method || "GET"} failed (${res.status}): ${detail}`);
  }
  // 204/202 = no body
  if (res.status === 204 || res.status === 202) return null;
  return res.json();
}

// ─── Read messages ───────────────────────────────────────────────────────────

/**
 * Fetch messages from the shared mailbox inbox.
 * @param {object} msalInstance
 * @param {object} opts
 * @param {number} [opts.top=50] — how many messages per page
 * @param {number} [opts.skip=0] — pagination offset
 * @param {string} [opts.filter] — OData $filter expression
 * @returns {Promise<{ messages: object[], totalCount: number|null }>}
 */
export async function fetchInboxMessages(msalInstance, { top = 50, skip = 0, filter } = {}) {
  const params = new URLSearchParams({
    $top: String(top),
    $skip: String(skip),
    $orderby: "receivedDateTime desc",
    $select: "id,subject,from,toRecipients,ccRecipients,receivedDateTime,bodyPreview,body,isRead,hasAttachments,conversationId,internetMessageId,importance,flag",
    $count: "true",
  });
  if (filter) params.set("$filter", filter);
  const url = `${GRAPH_BASE}/mailFolders/inbox/messages?${params}`;
  const data = await graphFetch(msalInstance, url, {
    headers: { ConsistencyLevel: "eventual" },
  });
  return {
    messages: data?.value || [],
    totalCount: data?.["@odata.count"] ?? null,
    nextLink: data?.["@odata.nextLink"] ?? null,
  };
}

/**
 * Fetch a single message by ID (full body).
 */
export async function fetchMessage(msalInstance, messageId) {
  const url = `${GRAPH_BASE}/messages/${messageId}?$select=id,subject,from,toRecipients,ccRecipients,receivedDateTime,body,isRead,hasAttachments,conversationId,internetMessageId,importance,flag`;
  return graphFetch(msalInstance, url);
}

/**
 * Fetch the full conversation thread for a message (all messages sharing
 * the same conversationId).
 */
export async function fetchConversationThread(msalInstance, conversationId) {
  const params = new URLSearchParams({
    $filter: `conversationId eq '${conversationId}'`,
    $orderby: "receivedDateTime asc",
    $top: "100",
    $select: "id,subject,from,toRecipients,ccRecipients,receivedDateTime,body,bodyPreview,isRead,hasAttachments,conversationId,importance",
  });
  const url = `${GRAPH_BASE}/messages?${params}`;
  const data = await graphFetch(msalInstance, url);
  return data?.value || [];
}

// ─── Reply ───────────────────────────────────────────────────────────────────

/**
 * Reply to a message in the shared mailbox.
 * @param {object} msalInstance
 * @param {string} messageId — the Graph message ID to reply to
 * @param {string} comment — the reply body (HTML)
 */
export async function replyToMessage(msalInstance, messageId, comment) {
  const url = `${GRAPH_BASE}/messages/${messageId}/reply`;
  await graphFetch(msalInstance, url, {
    method: "POST",
    body: JSON.stringify({ comment }),
  });
}

/**
 * Reply-all to a message in the shared mailbox.
 */
export async function replyAllToMessage(msalInstance, messageId, comment) {
  const url = `${GRAPH_BASE}/messages/${messageId}/replyAll`;
  await graphFetch(msalInstance, url, {
    method: "POST",
    body: JSON.stringify({ comment }),
  });
}

// ─── Manage ──────────────────────────────────────────────────────────────────

/**
 * Mark a message as read.
 */
export async function markAsRead(msalInstance, messageId) {
  const url = `${GRAPH_BASE}/messages/${messageId}`;
  await graphFetch(msalInstance, url, {
    method: "PATCH",
    body: JSON.stringify({ isRead: true }),
  });
}

/**
 * Mark a message as unread.
 */
export async function markAsUnread(msalInstance, messageId) {
  const url = `${GRAPH_BASE}/messages/${messageId}`;
  await graphFetch(msalInstance, url, {
    method: "PATCH",
    body: JSON.stringify({ isRead: false }),
  });
}

/**
 * Delete a message from the shared mailbox (moves to Deleted Items).
 */
export async function deleteMessage(msalInstance, messageId) {
  const url = `${GRAPH_BASE}/messages/${messageId}`;
  await graphFetch(msalInstance, url, { method: "DELETE" });
}

// ─── Classification helpers ──────────────────────────────────────────────────
// These run client-side on the fetched message data — no extra Graph calls.

const LOI_SUBJECT_PATTERNS = [
  /off-market.*acquisition/i,
  /letter of intent/i,
  /unsolicited offer/i,
  /regarding your property/i,
];

const CA_KEYWORDS = [
  "confidentiality agreement",
  "confidentiality",
  "non-disclosure",
  "nda",
  "sign ca",
  "ca link",
  "click here to sign",
  "confidential information memorandum",
  "cim",
];

const CA_URL_PATTERNS = [
  /confidential/i,
  /nda/i,
  /\bca\b/i,
  /docusign/i,
  /hellosign/i,
  /pandadoc/i,
  /adobe.*sign/i,
];

/**
 * Classify a message as 'outbound_reply' (response to an LOI we sent) or
 * 'inbound' (broker blast / new deal / general). Returns:
 * { type: 'outbound_reply' | 'inbound', caLinks: string[], brokerage: string|null }
 */
export function classifyMessage(msg, outreachContacts = []) {
  const fromAddr = msg.from?.emailAddress?.address?.toLowerCase() || "";
  const fromName = msg.from?.emailAddress?.name || "";
  const subject = msg.subject || "";
  const bodyText = (msg.body?.content || msg.bodyPreview || "").toLowerCase();

  // ── Is this a reply to an LOI we sent? ──
  // Check 1: sender's email matches a contact we've sent LOIs to.
  const matchesOutreach = outreachContacts.some(email =>
    email && fromAddr === email.toLowerCase()
  );
  // Check 2: subject line matches an LOI-style subject.
  const matchesLoiSubject = LOI_SUBJECT_PATTERNS.some(p => p.test(subject));
  // Check 3: it's a RE:/FWD: of an LOI subject.
  const isReply = /^(re|fw|fwd):/i.test(subject);
  const isOutboundReply = matchesOutreach || (isReply && matchesLoiSubject);

  // ── Extract CA / NDA links from the body ──
  const caLinks = [];
  // Find all URLs in the body.
  const urlRegex = /https?:\/\/[^\s<"']+/gi;
  const urls = (msg.body?.content || msg.bodyPreview || "").match(urlRegex) || [];
  for (const url of urls) {
    if (CA_URL_PATTERNS.some(p => p.test(url))) {
      caLinks.push(url);
    }
  }
  // Also check if CA keywords appear in the body text (even without a matching URL).
  const hasCAMention = CA_KEYWORDS.some(kw => bodyText.includes(kw.toLowerCase()));

  // ── Guess the brokerage from the sender's email domain ──
  let brokerage = null;
  const domain = fromAddr.split("@")[1] || "";
  const KNOWN_BROKERAGES = {
    "cbre.com": "CBRE",
    "jll.com": "JLL",
    "cushwake.com": "Cushman & Wakefield",
    "nmrk.com": "Newmark",
    "colliers.com": "Colliers",
    "marcusmillichap.com": "Marcus & Millichap",
    "berkadia.com": "Berkadia",
    "hfrgroup.com": "HFF",
    "eastdilsecured.com": "Eastdil Secured",
    "avison-young.com": "Avison Young",
    "lee-associates.com": "Lee & Associates",
    "ngkf.com": "Newmark",
    "svn.com": "SVN",
    "kidder.com": "Kidder Mathews",
    "triadanet.com": "Triada",
    "mfrealestategroup.com": "MF Real Estate",
  };
  brokerage = KNOWN_BROKERAGES[domain] || null;
  // Fallback: extract from the display name if it contains a brokerage name.
  if (!brokerage) {
    const nameUpper = fromName.toUpperCase();
    for (const [, name] of Object.entries(KNOWN_BROKERAGES)) {
      if (nameUpper.includes(name.toUpperCase())) { brokerage = name; break; }
    }
  }

  return {
    type: isOutboundReply ? "outbound_reply" : "inbound",
    caLinks,
    hasCAMention,
    brokerage,
  };
}
